import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useT } from "@/i18n/i18n";
import api, { formatApiError } from "@/lib/api";
import { PageTitle, Btn, Field, Input, Select, Modal, toast } from "@/pages/app/kit";
import { RiskBadge, DemoBadge } from "@/components/shared";
import { GitMerge, Factory, Truck } from "lucide-react";

export default function RescuePage() {
  const { t, lang } = useT();
  const L = (pl, en) => (lang === "pl" ? pl : en);
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [caps, setCaps] = useState([]);
  const [alloc, setAlloc] = useState(null);
  const [aForm, setAForm] = useState({ processor_id: "", quantity_sent: 0, process_type: "Freezing" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/rescue").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/processing/capacity").then((r) => setCaps(r.data)).catch(() => {}); }, []);

  const openAlloc = (crop) => { setAlloc(crop); setAForm({ processor_id: caps[0]?.id || "", quantity_sent: Math.min(crop.risk.remaining, 1000), process_type: caps[0]?.process_type || "Freezing" }); };

  const doAlloc = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/processing/allocate", { crop_id: alloc.id, processor_id: aForm.processor_id, process_type: aForm.process_type, quantity_sent: parseFloat(aForm.quantity_sent) || 0 });
      toast.success(L("Alokacja utworzona", "Allocation created")); setAlloc(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const requestTransport = async (crop) => {
    try {
      await api.post("/transport", { crop_id: crop.id, product: crop.product, quantity: crop.risk.remaining, origin: crop.location || crop.region, destination: "Regional Center", refrigeration: crop.refrigeration_required, region: crop.region });
      toast.success(L("Transport zamówiony", "Transport requested")); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const markReviewed = async (crop) => {
    try { await api.patch(`/crops/${crop.id}/status`, { status: "Verified", note: "Reviewed on rescue board" }); toast.success("OK"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageTitle title="Ratunek dla Plonu" subtitle={L("Plony zagrożone wymagające działania", "Crops at risk requiring action")} />
      <div className="grid gap-5 lg:grid-cols-2">
        {rows.length === 0 && <p className="text-mutedtext">{L("Brak plonów zagrożonych.", "No crops at risk.")}</p>}
        {rows.map((c) => (
          <div key={c.id} className="rounded-md border border-subtle bg-white p-5" data-testid={`rescue-item-${c.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-heading text-lg font-bold text-charcoal">{c.product} — {c.variety} {c.is_demo && <DemoBadge className="ml-1" />}</div>
                <div className="text-sm capitalize text-mutedtext">{c.region} · {c.owner_name}</div>
              </div>
              <RiskBadge priority={c.risk.priority} />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
              <KV label={L("Ilość","Qty")} v={`${c.expected_quantity.toLocaleString()} ${c.quantity_unit}`} />
              <KV label={L("Dni","Days")} v={c.risk.days_to_harvest} />
              <KV label={L("Min. cena","Min price")} v={`${c.min_viable_price}`} />
              <KV label={L("Świeże","Fresh")} v={(c.matched_fresh||0).toLocaleString()} />
              <KV label={L("Przetw.","Proc.")} v={(c.matched_processing||0).toLocaleString()} />
              <KV label={L("Ryzyko","At risk")} v={<span className="font-semibold text-crittext">{c.risk.remaining.toLocaleString()}</span>} />
            </div>
            <ul className="mt-3 space-y-0.5 border-t border-subtle pt-3 text-xs text-mutedtext">
              {c.risk.reasons.map((r, i) => <li key={i}>• {r}</li>)}
            </ul>
            <div className="mt-4 flex flex-wrap gap-2">
              <Btn variant="primary" icon={GitMerge} onClick={() => nav(`/app/matching?crop=${c.id}`)} data-testid={`rescue-match-${c.id}`}>{L("Dopasuj","Match")}</Btn>
              <Btn variant="outline" icon={Factory} onClick={() => openAlloc(c)} data-testid={`rescue-alloc-${c.id}`}>{L("Do przetwórcy","Processor")}</Btn>
              <Btn variant="outline" icon={Truck} onClick={() => requestTransport(c)}>{L("Transport","Transport")}</Btn>
              <Btn variant="ghost" onClick={() => markReviewed(c)}>{L("Sprawdzono","Reviewed")}</Btn>
            </div>
          </div>
        ))}
      </div>

      <Modal open={!!alloc} onClose={() => setAlloc(null)} title={L("Alokacja do przetwórcy","Allocate to processor")}>
        {alloc && (
          <form onSubmit={doAlloc} className="space-y-4">
            <Field label={L("Przetwórca / moc","Processor / capacity")} required>
              <Select value={aForm.processor_id} onChange={(e) => { const cap = caps.find((c)=>c.id===e.target.value); setAForm({ ...aForm, processor_id: e.target.value, process_type: cap?.process_type || aForm.process_type }); }}>
                {caps.map((c) => <option key={c.id} value={c.id}>{c.owner_name} — {c.process_type} ({c.available_quantity} kg)</option>)}
              </Select>
            </Field>
            <Field label={L("Ilość (kg)","Quantity (kg)")} required><Input type="number" value={aForm.quantity_sent} onChange={(e) => setAForm({ ...aForm, quantity_sent: e.target.value })} /></Field>
            <Btn type="submit" disabled={busy}>{busy?"…":L("Alokuj","Allocate")}</Btn>
          </form>
        )}
      </Modal>
    </div>
  );
}

function KV({ label, v }) {
  return <div><div className="text-xs text-mutedtext">{label}</div><div className="font-medium text-charcoal">{v}</div></div>;
}
